'use client';

import { c as _c } from "react/compiler-runtime";
import { Slider } from '@common/components';
import { SvgShareLink } from '@common/icon';
import { cn } from '@core/utils';
import { useRef, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES, SEARCH_PARAMS } from '@/constants';
import { useClipboard } from '@/hooks/useClipboard';
import { useAuthStore } from '@/stores/AuthStore';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE, trackGa4Event, trackGtmEvent } from '@/utils/analytics/gtm';
import type { IncomeBand, Recommendation, SavingsScenario } from '../_utils/youthSavingsCalc';
import { benefitPerHundred, calculateFutureSavings, calculateLeapAccount, resolveIncomeBand, resolveRecommendation } from '../_utils/youthSavingsCalc';
interface ShareContext {
  title: string;
  contentId: string;
}
interface YouthSavingsCalculatorProps {
  shareContext?: ShareContext;
}
const formatManwon = (value: number): string => `${Math.round(value).toLocaleString('ko-KR')}만원`;
const formatPeriod = (months: number): string => {
  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  if (years > 0 && restMonths > 0) {
    return `${years}년 ${restMonths}개월`;
  }
  if (years > 0) {
    return `${years}년`;
  }
  return `${restMonths}개월`;
};
const BAND_LABEL: Record<IncomeBand, string> = {
  preferential: '우대형 +12%',
  general: '일반형 +6%',
  taxFree: '비과세형',
  excess: '소득 초과 · 가입 불가'
};
export const YouthSavingsCalculator = t0 => {
  const $ = _c(89);
  const {
    shareContext
  } = t0;
  const [monthlyPayment, setMonthlyPayment] = useState(50);
  const [remainingMonths, setRemainingMonths] = useState(36);
  const [annualSalary, setAnnualSalary] = useState(3000);
  const hasTrackedCalculateRef = useRef(false);
  let t1;
  if ($[0] !== shareContext?.contentId || $[1] !== shareContext?.title) {
    t1 = fieldName => {
      if (hasTrackedCalculateRef.current) {
        return;
      }
      hasTrackedCalculateRef.current = true;
      trackGa4Event("calculate", {
        field_name: fieldName,
        content_name: shareContext?.title ?? "",
        content_id: shareContext?.contentId ?? ""
      });
    };
    $[0] = shareContext?.contentId;
    $[1] = shareContext?.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackCalculateOnce = t1;
  let band;
  let copy;
  let future;
  let leap;
  let recommendation;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let winnerBenefit;
  if ($[3] !== annualSalary || $[4] !== monthlyPayment || $[5] !== remainingMonths || $[6] !== shareContext) {
    band = resolveIncomeBand(annualSalary);
    leap = calculateLeapAccount(monthlyPayment, remainingMonths, band);
    future = calculateFutureSavings(monthlyPayment, band);
    recommendation = resolveRecommendation({
      remainingMonths,
      monthlyPayment,
      leap,
      future
    });
    copy = buildRecommendationCopy(recommendation, {
      remainingMonths,
      leapPerHundred: benefitPerHundred(leap),
      futurePerHundred: future ? benefitPerHundred(future) : 0
    });
    const winnerScenario = recommendation.winner === "future" ? future : leap;
    winnerBenefit = winnerScenario?.benefit ?? leap.benefit;
    t8 = "relative flex flex-col gap-16 mobile-tablet:flex-1";
    t9 = shareContext ? <ShareSection recommendation={recommendation} leap={leap} future={future} shareContext={shareContext} /> : null;
    t5 = "polite";
    t6 = "flex flex-col items-center gap-2 text-center";
    t7 = <span className="font-600 text-Gray-700 typo-body4">{copy.title}</span>;
    t2 = "font-700 tabular-nums text-B-500 typo-h3";
    t3 = "+";
    t4 = formatManwon(winnerBenefit);
    $[3] = annualSalary;
    $[4] = monthlyPayment;
    $[5] = remainingMonths;
    $[6] = shareContext;
    $[7] = band;
    $[8] = copy;
    $[9] = future;
    $[10] = leap;
    $[11] = recommendation;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = winnerBenefit;
  } else {
    band = $[7];
    copy = $[8];
    future = $[9];
    leap = $[10];
    recommendation = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
    winnerBenefit = $[20];
  }
  let t10;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t10 = <strong className={t2}>{t3}{t4}</strong>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== copy.body) {
    t11 = <p role="status" className="flex min-h-40 items-center break-keep font-400 text-Gray-600 typo-caption1">{copy.body}</p>;
    $[25] = copy.body;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t12 = <div aria-live={t5} className={t6}>{t7}{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = leap.monthlyPayment;
  let t14;
  if ($[33] !== remainingMonths) {
    t14 = formatPeriod(remainingMonths);
    $[33] = remainingMonths;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = `월 ${t13}만 · ${t14}`;
  const t16 = recommendation.winner === "leap";
  let t17;
  if ($[35] !== leap || $[36] !== t15 || $[37] !== t16) {
    t17 = <ScenarioPanel name={"\uCCAD\uB144\uB3C4\uC57D\uACC4\uC88C"} tag={"\uADF8\uB300\uB85C \uC720\uC9C0"} periodText={t15} scenario={leap} isWinner={t16} />;
    $[35] = leap;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = future ? `월 ${future.monthlyPayment}만 · 3년` : "";
  const t19 = recommendation.winner === "future";
  let t20;
  if ($[39] !== future || $[40] !== t18 || $[41] !== t19) {
    t20 = <ScenarioPanel name={"\uCCAD\uB144\uBBF8\uB798\uC801\uAE08"} tag={"\uAC08\uC544\uD0C0\uAE30"} periodText={t18} scenario={future} isWinner={t19} unavailableText={"\uC18C\uB4DD\uC774 \uB192\uC544 \uAC00\uC785 \uB300\uC0C1\uC774 \uC544\uB2C8\uC5D0\uC694"} />;
    $[39] = future;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t17 || $[44] !== t20) {
    t21 = <div className="grid grid-cols-2 gap-12">{t17}{t20}</div>;
    $[43] = t17;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = `${monthlyPayment}만원`;
  let t23;
  if ($[46] !== trackCalculateOnce) {
    t23 = next => {
      trackCalculateOnce("monthly_payment");
      setMonthlyPayment(next);
    };
    $[46] = trackCalculateOnce;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== monthlyPayment || $[49] !== t22 || $[50] !== t23) {
    t24 = <SliderField label={"\uB9E4\uB2EC \uB123\uC744 \uAE08\uC561"} valueText={t22} min={10} max={70} step={5} value={monthlyPayment} onValueChange={t23} />;
    $[48] = monthlyPayment;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== remainingMonths) {
    t25 = formatPeriod(remainingMonths);
    $[52] = remainingMonths;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== trackCalculateOnce) {
    t26 = next_0 => {
      trackCalculateOnce("remaining_months");
      setRemainingMonths(next_0);
    };
    $[54] = trackCalculateOnce;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== remainingMonths || $[57] !== t25 || $[58] !== t26) {
    t27 = <SliderField label={"\uB0A8\uC740 \uAE30\uAC04"} valueText={t25} min={6} max={60} step={1} value={remainingMonths} onValueChange={t26} />;
    $[56] = remainingMonths;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== annualSalary) {
    t28 = annualSalary >= 8000 ? "8,000\uB9CC\uC6D0+" : `${annualSalary.toLocaleString("ko-KR")}만원`;
    $[60] = annualSalary;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  const t29 = BAND_LABEL[band];
  const t30 = band === "excess" ? "warning" : "muted";
  let t31;
  if ($[62] !== trackCalculateOnce) {
    t31 = next_1 => {
      trackCalculateOnce("annual_salary");
      setAnnualSalary(next_1);
    };
    $[62] = trackCalculateOnce;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== annualSalary || $[65] !== t28 || $[66] !== t29 || $[67] !== t30 || $[68] !== t31) {
    t32 = <SliderField label={"\uC791\uB144 \uC5F0\uC18C\uB4DD (\uC138\uC804)"} valueText={t28} hint={t29} hintTone={t30} min={0} max={8000} step={100} value={annualSalary} onValueChange={t31} />;
    $[64] = annualSalary;
    $[65] = t28;
    $[66] = t29;
    $[67] = t30;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t24 || $[71] !== t27 || $[72] !== t32) {
    t33 = <div className="flex flex-col gap-12 border-t border-Gray-100 pt-16">{t24}{t27}{t32}</div>;
    $[70] = t24;
    $[71] = t27;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== recommendation.winner || $[75] !== shareContext?.contentId || $[76] !== shareContext?.title || $[77] !== winnerBenefit) {
    t34 = <CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname} className="font-600 text-Gray-700 underline typo-caption1" onClick={() => {
      const baseParams = {
        btn_name: "\uB0B4\uAC8C\uB9DE\uB294\uC815\uCC45\uB354\uBCF4\uAE30",
        content_name: shareContext?.title ?? "",
        content_id: shareContext?.contentId ?? ""
      };
      trackGtmEvent(GTM_EVENT_TYPE.CLICK_BTN, baseParams);
      trackGa4Event("calculator_cta", {
        ...baseParams,
        recommend_result: recommendation.winner,
        benefit_amount: String(winnerBenefit)
      });
    }}>내게 맞는 정책 더 보기</CustomLink>;
    $[74] = recommendation.winner;
    $[75] = shareContext?.contentId;
    $[76] = shareContext?.title;
    $[77] = winnerBenefit;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <p className="break-keep text-center font-400 text-Gray-400 typo-caption2">참고용 시뮬레이션이에요. 실제 금액은 가입 전 공식 안내를 확인하세요.</p>;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t34) {
    t36 = <div className="mt-auto flex flex-col items-center gap-8 pt-16">{t34}{t35}</div>;
    $[80] = t34;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t12 || $[83] !== t21 || $[84] !== t33 || $[85] !== t36 || $[86] !== t8 || $[87] !== t9) {
    t37 = <div className={t8}>{t9}{t12}{t21}{t33}{t36}</div>;
    $[82] = t12;
    $[83] = t21;
    $[84] = t33;
    $[85] = t36;
    $[86] = t8;
    $[87] = t9;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  return t37;
};
interface ShareSectionProps {
  recommendation: Recommendation;
  leap: SavingsScenario;
  future: SavingsScenario | null;
  shareContext: ShareContext;
}
const ShareSection = t0 => {
  const $ = _c(16);
  const {
    recommendation,
    leap,
    future,
    shareContext
  } = t0;
  const copyToClipboard = useClipboard();
  const memberCode = useAuthStore(_temp);
  let t1;
  if ($[0] !== future || $[1] !== leap || $[2] !== recommendation.winner) {
    t1 = recommendation.winner === "future" && future ? `청년미래적금 갈아타기 vs 도약계좌 유지, 내 조건으론 갈아타기가 +${formatManwon(future.benefit)} 유리했어요. 조건만 넣으면 1분 만에 계산돼요.` : `청년미래적금 갈아타기 vs 도약계좌 유지, 내 조건으론 도약계좌 유지가 +${formatManwon(leap.benefit)} 유리했어요. 조건만 넣으면 1분 만에 계산돼요.`;
    $[0] = future;
    $[1] = leap;
    $[2] = recommendation.winner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shareSummary = t1;
  let t2;
  if ($[4] !== copyToClipboard || $[5] !== future || $[6] !== leap || $[7] !== memberCode || $[8] !== recommendation.winner || $[9] !== shareContext.contentId || $[10] !== shareContext.title || $[11] !== shareSummary) {
    t2 = async () => {
      const shareUrl = (() => {
        const url = new URL("https://www.welfarehello.com/api/redirect/019ef327-5b79-794b-a38a-99b594026806");
        url.searchParams.set(SEARCH_PARAMS.ENTER_TYPE, "share");
        if (memberCode) {
          url.searchParams.set(SEARCH_PARAMS.SHARE_BY, memberCode);
        }
        return url.href;
      })();
      const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
      const winnerScenario = recommendation.winner === "future" ? future : leap;
      const benefitAmount = winnerScenario?.benefit ?? leap.benefit;
      const baseParams = {
        method: canNativeShare ? "\uACF5\uC720\uD558\uAE30" : "\uB9C1\uD06C\uBCF5\uC0AC",
        content_name: shareContext.title,
        content_category: CONTENT_CATEGORY.POLICY_NEWS,
        content_id: shareContext.contentId
      };
      trackGtmEvent(GTM_EVENT_TYPE.SHARE, baseParams);
      trackGa4Event("share", {
        ...baseParams,
        recommend_result: recommendation.winner,
        benefit_amount: String(benefitAmount)
      });
      if (canNativeShare) {
        try {
          await navigator.share({
            title: shareContext.title,
            text: shareSummary,
            url: shareUrl
          });
        } catch {}
        return;
      }
      copyToClipboard({
        value: `${shareSummary}\n${shareUrl}`,
        message: "\uB9C1\uD06C\uB97C \uBCF5\uC0AC\uD588\uC5B4\uC694. \uCE5C\uAD6C\uC5D0\uAC8C \uBD99\uC5EC\uB123\uC5B4 \uBCF4\uC138\uC694."
      });
    };
    $[4] = copyToClipboard;
    $[5] = future;
    $[6] = leap;
    $[7] = memberCode;
    $[8] = recommendation.winner;
    $[9] = shareContext.contentId;
    $[10] = shareContext.title;
    $[11] = shareSummary;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleShare = t2;
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SvgShareLink className="size-20" />;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== handleShare) {
    t4 = <button onClick={handleShare} aria-label={"\uACB0\uACFC \uACF5\uC720\uD558\uAE30"} className="absolute right-0 top-0 z-10 flex size-40 items-center justify-center rounded-medium text-Gray-600 transition-colors hover:bg-Gray-50 hover:text-B-500">{t3}</button>;
    $[14] = handleShare;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
interface SliderFieldProps {
  label: string;
  valueText: string;
  hint?: string;
  hintTone?: 'muted' | 'warning';
  min: number;
  max: number;
  step: number;
  value: number;
  onValueChange: (next: number) => void;
}
const SliderField = t0 => {
  const $ = _c(26);
  const {
    label,
    valueText,
    hint,
    hintTone: t1,
    min,
    max,
    step,
    value,
    onValueChange
  } = t0;
  const hintTone = t1 === undefined ? "muted" : t1;
  let t2;
  if ($[0] !== hint || $[1] !== hintTone) {
    t2 = hint ? <span className={cn("ml-6 font-600", hintTone === "warning" ? "text-R-600" : "text-Gray-400")}>{hint}</span> : null;
    $[0] = hint;
    $[1] = hintTone;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label || $[4] !== t2) {
    t3 = <span className="font-500 text-Gray-600 typo-caption1">{label}{t2}</span>;
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== valueText) {
    t4 = <span className="whitespace-nowrap font-700 tabular-nums text-Gray-900 typo-body3">{valueText}</span>;
    $[6] = valueText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex items-baseline justify-between gap-12">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== value) {
    t6 = [value];
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onValueChange) {
    t7 = next => onValueChange(next[0]);
    $[13] = onValueChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== label || $[16] !== max || $[17] !== min || $[18] !== step || $[19] !== t6 || $[20] !== t7 || $[21] !== valueText) {
    t8 = <Slider aria-label={label} aria-valuetext={valueText} min={min} max={max} step={step} value={t6} onValueChange={t7} />;
    $[15] = label;
    $[16] = max;
    $[17] = min;
    $[18] = step;
    $[19] = t6;
    $[20] = t7;
    $[21] = valueText;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t5 || $[24] !== t8) {
    t9 = <div className="flex flex-col gap-8">{t5}{t8}</div>;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
interface ScenarioPanelProps {
  name: string;
  tag: string;
  periodText: string;
  scenario: SavingsScenario | null;
  isWinner: boolean;
  unavailableText?: string;
}
const ScenarioPanel = t0 => {
  const $ = _c(19);
  const {
    name,
    tag,
    periodText,
    scenario,
    isWinner,
    unavailableText
  } = t0;
  const t1 = isWinner ? "border-B-300 bg-B-25 shadow-sm ring-2 ring-B-200" : "border-Gray-100 bg-Gray-50 shadow-none ring-2 ring-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex flex-col gap-4 rounded-medium border-1 p-12 transition duration-300", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== name) {
    t3 = <span className="font-700 text-Gray-900 typo-body3">{name}</span>;
    $[2] = name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isWinner ? "bg-B-500 text-Gray-white" : "bg-Gray-100 text-Gray-500";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("inline-flex w-fit rounded-pill px-8 py-2 font-600 transition-colors duration-300 typo-caption2", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5 || $[7] !== tag) {
    t6 = <span className={t5}>{tag}</span>;
    $[6] = t5;
    $[7] = tag;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isWinner || $[10] !== periodText || $[11] !== scenario || $[12] !== unavailableText) {
    t7 = scenario ? <><strong className={cn("mt-4 font-700 tabular-nums", isWinner ? "text-B-500 typo-h6" : "text-Gray-500 typo-h7")}>{formatManwon(scenario.total)}</strong><span className="font-400 text-Gray-500 typo-caption2">{periodText}</span><div aria-hidden={true} className="mt-4 flex h-10 overflow-hidden rounded-pill bg-Gray-100"><div className="bg-Gray-300" style={{
          width: `${scenario.principal / scenario.total * 100}%`
        }} /><div className={cn("transition-colors duration-300 ease-out", isWinner ? "bg-B-400" : "bg-Gray-400")} style={{
          width: `${scenario.benefit / scenario.total * 100}%`
        }} /></div><p className="break-keep font-400 tabular-nums text-Gray-600 typo-caption2">원금 {formatManwon(scenario.principal)}</p><p className="break-keep font-400 tabular-nums text-Gray-500 typo-caption2">정부 {formatManwon(scenario.government)} · 이자{" "}{formatManwon(scenario.interest)}</p><p className="mt-2 break-keep font-600 tabular-nums typo-caption1">혜택{" "}<b className={cn("font-700", isWinner ? "text-B-500" : "text-Gray-700")}>+{formatManwon(scenario.benefit)}</b></p></> : <p className="mt-4 break-keep font-600 text-Gray-500 typo-body4">{unavailableText}</p>;
    $[9] = isWinner;
    $[10] = periodText;
    $[11] = scenario;
    $[12] = unavailableText;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={t2}>{t3}{t6}{t7}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
const buildRecommendationCopy = (recommendation: Recommendation, values: {
  remainingMonths: number;
  leapPerHundred: number;
  futurePerHundred: number;
}): {
  title: string;
  body: string;
} => {
  switch (recommendation.reason) {
    case 'incomeExcess':
      return {
        title: '청년도약계좌를 유지하세요',
        body: '소득이 높아 청년미래적금은 가입 대상이 아니에요. 지금 계좌를 그대로 두는 게 좋아요.'
      };
    case 'nearMaturity':
      return {
        title: '청년도약계좌를 유지하세요',
        body: `만기가 ${formatPeriod(values.remainingMonths)} 정도밖에 안 남았어요. 지금 갈아타면 3년을 다시 시작해서 손해예요.`
      };
    case 'highCapacity':
      return {
        title: '청년도약계좌를 유지하세요',
        body: '매달 60만원 넘게 넣을 수 있다면, 한도가 더 큰 도약계좌(월 70만·5년)가 최종 목돈에서 앞서요.'
      };
    case 'futureHigherReturn':
      return {
        title: '청년미래적금으로 갈아타세요',
        body: `100만원당 약 ${values.futurePerHundred}만원이 붙어요. 도약계좌(약 ${values.leapPerHundred}만원)보다 많아 갈아타는 게 이득이에요.`
      };
    case 'leapHigherReturn':
      return {
        title: '청년도약계좌를 유지하세요',
        body: `100만원당 약 ${values.leapPerHundred}만원이 붙어요. 미래적금(약 ${values.futurePerHundred}만원)보다 많아 그대로 두는 게 이득이에요.`
      };
  }
};
function _temp(state) {
  return state.myInfo?.memberCode;
}